// Auth interfaces
export interface LoginCredentials {
    email: string;
    password: string;
}

// Fleet subtype for industry-specific compliance checks
export type FleetSubtype = 'bus' | 'coach' | 'haulage' | 'car_fleet' | 'general';

export interface AuthResponse {
    token: string;
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        organisationId: string;
        organisation?: {
            id: string;
            name: string;
            automatedChecks?: boolean;
            subtype?: FleetSubtype;
        };
    };
    mustChangePassword?: boolean;
    passwordExpired?: boolean;
}

// Site interfaces
export interface Site {
    id: string;
    organisationId: string;
    name: string;
    code: string;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    postcode?: string | null;
    active: boolean;
    driverCount?: number;
    createdAt: string;
    updatedAt: string;
}

export interface CreateSiteRequest {
    name: string;
    code: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postcode?: string;
}

export interface UpdateSiteRequest {
    name: string;
    code: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postcode?: string;
    active?: boolean;
}

export interface SitesResponse {
    message: string;
    data: Site[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// Site Risk interfaces (Multi-Industry Driver Compliance)
// Supports: Bus, Coach, Haulage, Car Fleet organisations
// Methodology: Site Score = Average of all driver effective risk scores
export type RiskLevel = 'low' | 'medium' | 'high';
export type TrendDirection = 'improving' | 'stable' | 'worsening';

// Breakdown category definition - metadata from backend (single source of truth)
export interface BreakdownCategoryDefinition {
    key: string;           // The breakdown category key (e.g., 'invalidOrDisqualified')
    filter: string;        // URL parameter for filtering (e.g., 'invalid')
    label: string;         // Display label (e.g., 'invalid')
    severity: IssueSeverity;
    icon: IssueIconType;
    priority: number;      // Display order (lower = higher priority)
}

// Breakdown item for display - shows counts and percentages
export interface SiteRiskBreakdownItem {
    count: number;
    percentage: number;
    driverIds: string[];
}

// Site risk breakdown - counts for click-through functionality
export interface SiteRiskBreakdown {
    invalidOrDisqualified: SiteRiskBreakdownItem;
    seriousOffences: SiteRiskBreakdownItem;
    over6Points: SiteRiskBreakdownItem;
    neverChecked: SiteRiskBreakdownItem;
    overdueRecheck: SiteRiskBreakdownItem;
    noConsent: SiteRiskBreakdownItem;
    expired: SiteRiskBreakdownItem;
    expiringSoon: SiteRiskBreakdownItem;
    highRisk: SiteRiskBreakdownItem;
}

export interface SiteRiskScore {
    siteId: string;
    siteName: string;
    siteCode: string;
    /** Average of all driver effective risk scores (lower is better) */
    totalScore: number;
    breakdown: SiteRiskBreakdown;
    totalDrivers: number;
    driversWithRecentChecks: number;
    driversNeverChecked: number;
    driversOverdue: number;
    riskLevel: RiskLevel;
}

export interface SiteRiskLeaderboardEntry {
    rank: number;
    siteId: string;
    siteName: string;
    siteCode: string;
    totalScore: number;
    totalDrivers: number;
    trend: TrendDirection;
    previousScore: number | null;
    scoreChange: number | null;
    riskLevel: RiskLevel;
    // Breakdown counts for all categories (dynamically keyed)
    breakdown: Record<string, number>;
}

// Issue definition from backend (for frontend grouping and display)
export interface IssueDefinition {
    code: string;
    severity: IssueSeverity;
    icon: IssueIconType;
    label: string;
    filterLabel: string;
}

export interface SiteRiskLeaderboardResponse {
    message: string;
    data: SiteRiskLeaderboardEntry[];
    totalSites: number;
    // Single source of truth for breakdown category metadata (from backend)
    breakdownCategories: BreakdownCategoryDefinition[];
    // Issue definitions filtered by org subtype (for frontend issue grouping)
    issueDefinitions: IssueDefinition[];
}

export interface SiteRiskScoreResponse {
    message: string;
    data: SiteRiskScore;
}

export interface SiteRiskHistoryPoint {
    date: string;
    totalScore: number;
    totalDrivers: number;
    driversWithRecentChecks: number;
    driversNeverChecked: number;
    driversOverdue: number;
}

export interface SiteRiskTrend {
    siteId: string;
    siteName: string;
    currentScore: number;
    previousScore: number | null;
    trend: TrendDirection;
    history: SiteRiskHistoryPoint[];
}

export interface SiteRiskTrendResponse {
    message: string;
    data: SiteRiskTrend;
}

// Aggregate trends for dashboard charts
export interface SiteRiskTrendsData {
    fleetTrend: { date: string; avgScore: number; totalDrivers: number }[];
    riskDistribution: {
        low: number;
        medium: number;
        high: number;
        sites: { siteId: string; siteName: string; riskLevel: 'low' | 'medium' | 'high'; score: number }[];
    };
    expiryForecast: {
        licence: { expired: number; next30: number; next60: number; next90: number };
        cpc: { expired: number; next30: number; next60: number; next90: number };
        tacho: { expired: number; next30: number; next60: number; next90: number };
        consent: { expired: number; next30: number; next60: number; next90: number };
    };
    siteComparison: {
        top5: { siteId: string; siteName: string; history: { date: string; score: number }[] }[];
        bottom5: { siteId: string; siteName: string; history: { date: string; score: number }[] }[];
    };
}

export interface SiteRiskTrendsResponse {
    message: string;
    data: SiteRiskTrendsData;
}

// Driver interfaces
export interface Driver {
    id: string;
    firstName: string;
    lastName: string;
    licenceNumber: string;
    email: string;
    dateOfBirth: string;
    addressLine1?: string;
    addressLine2?: string;
    phone?: string;
    city?: string;
    postcode?: string;
    siteId?: string | null;
    site?: Site;
    riskLevel?: 'low' | 'medium' | 'high';
    lastLicenceCheck?: string;
    lastCheckDate?: string | null;
    lastCheckWasSuccessful?: boolean | null;
    daysSinceLastCheck?: number | null;
    licenceExpiryDate?: string | null;
    cpcExpiryDate?: string | null;
    consentProvided: boolean;
    consentDate?: string;
    consentExpiry?: string;
    consentReference?: string;
    consentSentDate?: string;
    consentSentBy?: string;
    consentReminderCount?: number;
    consentStatus?: ConsentStatusResponse;
    consentStatusLoading?: boolean;
    emailDeliveryStatus?: 'delivered' | 'failed' | 'pending' | 'unknown';
    lastEmailDate?: string;
    emailRetryCount?: number;
    active: boolean;
    createdAt: string;
    updatedAt: string;
    // Pre-computed issues from backend (consistent with site-risk breakdown)
    issues?: DriverIssue[];
    // Pre-computed clear to drive status from backend
    clearToDrive?: boolean;
}

// Driver issue types - metadata comes from backend (no manual lists to maintain)
export type IssueSeverity = 'critical' | 'warning' | 'info';
export type IssueIconType = 'error' | 'ban' | 'warning' | 'clock' | 'help' | 'info';

export interface DriverIssue {
    code: string;           // Dynamic - new codes automatically work
    severity: IssueSeverity;
    icon: IssueIconType;    // Icon type from backend
    label: string;          // Short label for badge
    filterLabel: string;    // Longer label for filter UI
    description?: string;   // Full description from backend (for tooltips)
    value?: number;         // Dynamic values (e.g., points count)
}

export interface CreateDriverRequest {
    firstName: string;
    lastName: string;
    licenceNumber: string;
    email: string;
    dateOfBirth: string;
    addressLine1: string;
    addressLine2?: string;
    phone?: string;
    city: string;
    postcode: string;
    siteId?: string;
}

export interface UpdateDriverRequest {
    firstName: string;
    lastName: string;
    licenceNumber: string;
    email: string;
    dateOfBirth: string;
    phone?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postcode: string;
    siteId?: string;
}

// Issue count for filter dropdown (from backend aggregation)
export interface IssueCount {
    code: string;
    label: string;
    severity: IssueSeverity;
    count: number;
}

export interface DriversResponse {
    message: string;
    data: Driver[];
    totalCount: number;
    counts: {
        // New compliance-focused counts (7 stat tiles)
        total: number;
        clearToDrive: number;
        notClear: number;
        expiringLicence: number;
        expiringCpc: number;
        expiringTacho: number;
        highRisk: number;
        overdueChecks: number;
        consentIssues: number;
        // Legacy consent counts (kept for backward compatibility)
        activeConsent: number;
        pendingConsent: number;
        noConsent: number;
        declinedConsent: number;
        needsAttention: number;
    };
    // Issue type counts across ALL drivers (for filter dropdown)
    issueCounts: IssueCount[];
    // Breakdown category definitions (from backend - single source of truth)
    breakdownCategories: BreakdownCategoryDefinition[];
    page: number;
    pageSize: number;
    totalPages: number;
}

// Licence Check interfaces
export interface LicenceCheckRequest {
    driverId: string;
    includeCPC?: boolean;
}

export interface RiskFactor {
    text: string;
    score: number;
    severity: 'critical' | 'urgent' | 'warning' | 'info';
}

export interface LicenceCheckResult {
    id: string;
    driverId: string;
    checkDate: string;
    valid: boolean;
    status: string;
    statusCode: string;
    licenceType: string;
    categories: Array<{
        categoryCode: string;
        categoryLegalLiteral: string;
        categoryType: string;
        fromDate?: string;
        expiryDate: string;
        restrictions?: Array<{
            restrictionCode: string;
            restrictionLiteral: string;
        }>;
    }>;
    penaltyPoints: number;
    endorsements: Array<{
        offenceCode: string;
        offenceLegalLiteral: string;
        convictionDate: string;
        offenceDate?: string;
        penaltyPoints?: number;
        penaltyPointsExpiryDate?: string;
        disqualification?: {
            years?: number;
            months?: number;
            startDate?: string;
        };
    }>;
    restrictions: string[];
    expiryDate: Date | null;
    issueDate: Date | null;
    riskLevel: string;
    riskAssessment: {
        level: 'low' | 'medium' | 'high';
        score: number;
        factors: Array<string | RiskFactor>;
        recommendations: string[];
        nextCheckDue: string;
    };
    cpcDetails?: any;
    tachographDetails?: any;
    rawResponse: any;
    createdAt?: string;
    isAutomated?: boolean;
    retryCount?: number;
    retryOfCheckId?: string | null;
}


export interface LicenceCheckResponse {
    message: string;
    data: LicenceCheckResult;
}

export interface LicenceCheckHistoryResponse {
    message: string;
    data: LicenceCheckResult[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// Blocker issue preventing clear to drive
export interface ClearToDriveBlocker {
    code: string;
    label: string;
    description: string;
}

export interface LatestLicenceCheckResponse {
    message: string;
    data: {
        driver: {
            id: string;
            fullName?: string;
            licenceNumber?: string;
            lastLicenceCheck?: string;
            riskLevel?: string;
            // Pre-computed clear to drive status
            clearToDrive?: boolean;
            issues?: DriverIssue[];
            clearToDriveBlockers?: ClearToDriveBlocker[];
        };
        latestCheck: LicenceCheckResult | null;
    };
}

// Dashboard interfaces
export interface DashboardStats {
    totalDrivers: number;
    activeDrivers: number;
    riskLevels: {
        low: number;
        medium: number;
        high: number;
    };
    checksThisMonth: number;
    checksToday: number;
    averageRiskScore: number;
    driversWithConsent: number;
    driversWithoutConsent: number;
    driversEverChecked: number;
    driversWithRecentChecks: number;
    consentExpiring: number;
    clearToDrive: number;
    notClearToDrive: number;
    lastUpdated: string;
}

export interface DriverDue {
    id: string;
    firstName: string;
    lastName: string;
    licenceNumber: string;
    riskLevel: 'low' | 'medium' | 'high';
    nextCheckDue: string;
    daysOverdue: number;
    reason: string;
}

export interface RecentActivity {
    id: string;
    driverName: string;
    licenceNumber: string;
    checkDate: string;
    riskLevel: 'low' | 'medium' | 'high';
    riskScore: number;
    status: string;
    checkedBy: string;
    isAutomated?: boolean;
}

// Automated Licence Checking interfaces
export interface LicenceCheckSettings {
    id: string;
    organisationId: string;
    enabled: boolean;
    defaultFrequencyMonths: number;
    preExpiryCheckEnabled: boolean;
    preExpiryCheckDays: number;
    highRiskFrequencyMonths: number;
    mediumRiskFrequencyMonths: number;
    lowRiskFrequencyMonths: number;
    preferredCheckTime: string;
    timezone: string;
    batchSize: number;
    batchDelaySeconds: number;
    maxRetries: number;
    retryDelayHours: number;
    immediateAlertsEnabled: boolean;
    dailyDigestEnabled: boolean;
    dailyDigestTime: string;
    weeklySummaryEnabled: boolean;
    weeklySummaryDay: number;
    notificationEmailRecipients: string[];
    autoCheckOnConsent: boolean;
    defaultIncludeCPC: boolean;
    defaultIncludeTacho: boolean;
    lastRunDate?: string;
    nextScheduledRun?: string;
    totalChecksPerformed: number;
    createdAt: string;
    updatedAt: string;
}

export interface UpdateLicenceCheckSettingsRequest {
    enabled?: boolean;
    defaultFrequencyMonths?: number;
    preExpiryCheckEnabled?: boolean;
    preExpiryCheckDays?: number;
    highRiskFrequencyMonths?: number;
    mediumRiskFrequencyMonths?: number;
    lowRiskFrequencyMonths?: number;
    preferredCheckTime?: string;
    timezone?: string;
    batchSize?: number;
    batchDelaySeconds?: number;
    maxRetries?: number;
    retryDelayHours?: number;
    immediateAlertsEnabled?: boolean;
    dailyDigestEnabled?: boolean;
    dailyDigestTime?: string;
    weeklySummaryEnabled?: boolean;
    weeklySummaryDay?: number;
    notificationEmailRecipients?: string[];
    autoCheckOnConsent?: boolean;
    defaultIncludeCPC?: boolean;
    defaultIncludeTacho?: boolean;
}

export interface TestSchedulerResponse {
    totalDrivers: number;
    eligible: {
        count: number;
        riskBreakdown: {
            high: number;
            medium: number;
            low: number;
            unknown: number;
        };
    };
    excluded: {
        noConsent: number;
        inactive: number;
    };
    settings: {
        enabled: boolean;
        highRiskFrequency: string;
        mediumRiskFrequency: string;
        lowRiskFrequency: string;
        defaultFrequency: string;
        nextScheduledRun: string | null;
        preferredCheckTime: string;
    };
}

export interface RunNowResponse {
    message: string;
    bulkOperationId: string;
    driversQueued: number;
}

// Scoring Configuration interfaces (per-organisation scoring overrides)
export interface ScoringConfigField {
    fieldName: string;
    currentValue: number;
    defaultValue: number;
    isCustomized: boolean;
    category: string;
    warningText?: string;
}

export interface ScoringConfigResponse {
    organisationId: string;
    subtype: FleetSubtype;
    subtypeLabel: string;
    fields: ScoringConfigField[];
    riskLevelThresholds: {
        lowMediumBoundary: number;
        mediumHighBoundary: number;
        isLowMediumCustomized: boolean;
        isMediumHighCustomized: boolean;
    };
    automationFrequencies: {
        highRiskMonths: number;
        mediumRiskMonths: number;
        lowRiskMonths: number;
        isHighRiskCustomized: boolean;
        isMediumRiskCustomized: boolean;
        isLowRiskCustomized: boolean;
    };
    hasAnyCustomizations: boolean;
    lastModifiedAt: string | null;
    lastModifiedBy: string | null;
}

export interface UpdateScoringConfigRequest {
    [key: string]: number | null | undefined;
}

export interface ScoringConfigUpdateResponse {
    message: string;
    effectiveConfig: Record<string, number>;
}

export interface ScoringConfigResetResponse {
    message: string;
    effectiveConfig: Record<string, number>;
}

// User Management interfaces
export interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: 'super_admin' | 'admin' | 'manager' | 'compliance' | 'user';
    isActive: boolean;
    organisationId: string;
    invitationToken: string | null;
    invitationExpiry: Date | null;
    invitedBy: string | null;
    lastLogin: Date | null;
    createdAt: Date;
    updatedAt: Date;
    organisation?: {
        id: string;
        name: string;
        automatedChecks?: boolean;
        subtype?: FleetSubtype;
    };
}

export interface UsersResponse {
    message: string;
    data: User[];
    counts: {
        activeUsers: number;
        pendingInvites: number;
        expiredInvites: number;
    };
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// Consent interfaces
export interface ConsentFormData {
    token: string;
    driver: {
        id: string;
        firstName: string;
        lastName: string;
        licenceNumber: string;
        dateOfBirth: string;
        email: string;
        phone?: string;
        addressLine1: string;
        addressLine2?: string;
        city: string;
        postcode: string;
    };
    organisation: {
        name: string;
        contactEmail: string;
        contactPhone?: string;
    };
    expiresAt: string;
    status: 'needed' | 'sent' | 'opened' | 'active' | 'withdrawn' | 'expired' | 'renewal_sent';
}

// Systen Health interfaces
export interface SystemHealthResponse {
    message: string;
    data: {
        overallHealth: 'healthy' | 'degraded' | 'unhealthy';
        redisHealth: 'healthy' | 'degraded' | 'unhealthy';
        queueStats: {
            consent: QueueStats;
            email: QueueStats;
            cleanup: QueueStats;
        };
        summary: {
            totalActiveJobs: number;
            totalFailedJobs: number;
            totalWaitingJobs: number;
            totalCompletedJobs: number;
        };
        issues: string[];
        lastChecked: string;
        backgroundJobsEnabled: boolean;
    };
}

export interface QueueStats {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
}

export interface JobStatsResponse {
    message: string;
    data: {
        queueStats: {
            consent: QueueStats;
            email: QueueStats;
            cleanup: QueueStats;
        };
        summary: {
            totalActiveJobs: number;
            totalFailedJobs: number;
            totalWaitingJobs: number;
            totalCompletedJobs: number;
        };
        lastUpdated: string;
    };
}

export interface ManualJobResponse {
    message: string;
    data: {
        success: boolean;
        message: string;
        data?: any;
    };
}

// Email Delivery Analytics interfaces
export interface EmailDeliveryLog {
    id: string;
    sgMessageId: string;
    recipientEmail: string;
    emailType: 'consent_request' | 'consent_confirmation' | 'consent_declined' | 'consent_withdrawn' | 'expiry_warning' | 'admin_notification';
    driverId?: string;
    deliveryStatus: 'sent' | 'delivered' | 'bounced' | 'dropped' | 'spam' | 'unsubscribed' | 'deferred';
    bounceReason?: string;
    retryCount: number;
    lastRetryAt?: string;
    sentAt: string;
    deliveredAt?: string;
    metadata?: any;
}

export interface EmailAnalyticsResponse {
    message: string;
    data: {
        summary: {
            totalEmails: number;
            deliveredEmails: number;
            failedEmails: number;
            deliveryRate: number;
            avgRetryCount: number;
        };
        recentLogs: EmailDeliveryLog[];
        statusBreakdown: {
            [key: string]: number;
        };
        typeBreakdown: {
            [key: string]: number;
        };
        lastUpdated: string;
    };
}

export interface DriverEmailHistoryResponse {
    message: string;
    data: {
        driver: {
            id: string;
            firstName: string;
            lastName: string;
            email: string;
        };
        emailHistory: EmailDeliveryLog[];
        summary: {
            totalEmails: number;
            successfulEmails: number;
            failedEmails: number;
            pendingEmails: number;
            lastEmailDate?: string;
        };
    };
}

export interface ConsentSubmissionData {
    signature: string; // Base64 signature image
    ipAddress?: string;
    userAgent?: string;
    updatedDriverData?: {
        email?: string;
        phone?: string;
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        postcode?: string;
    };
}

export interface ConsentStatusResponse {
    driver: {
        id: string;
        firstName: string;
        lastName: string;
        consentProvided: boolean;
        consentDate?: string;
        consentExpiry?: string;
    };
    tokens: Array<{
        id: string;
        status: string;
        createdAt: string;
        expiresAt: string;
        usedAt?: string;
        trackingEvents?: Array<{
            eventType: string;
            timestamp: string;
            metadata?: {
                eventSubtype?: string;
                reminderCount?: number;
                [key: string]: any;
            };
        }>;
    }>;
}

export interface SendConsentRequest {
    driverId: string;
}

// Bulk Operations interfaces
export interface CSVValidationResult {
    success: boolean;
    errors?: string[];
    duplicates?: string[];
    preview?: Array<{
        firstName: string;
        lastName: string;
        licenceNumber: string;
        email: string;
        dateOfBirth: string;
        phone?: string;
        addressLine1: string;
        addressLine2?: string;
        city: string;
        postcode: string;
    }>;
    totalRows?: number;
    validRows?: number;
    invalidRows?: number;
}

export interface CSVUploadResponse {
    message: string;
    data: CSVValidationResult;
}

export interface BulkImportRequest {
    drivers: Array<{
        firstName: string;
        lastName: string;
        licenceNumber: string;
        email: string;
        dateOfBirth: string;
        phone?: string;
        addressLine1: string;
        addressLine2?: string;
        city: string;
        postcode: string;
    }>;
}

export interface BulkOperationResponse {
    message: string;
    data: {
        operationId: string;
        totalDrivers: number;
        estimatedTime: string;
    };
}

export interface BulkLicenceCheckRequest {
    driverIds?: string[];
    siteIds?: string[];
}

export interface BulkConsentSendRequest {
    driverIds?: string[];
    siteIds?: string[];
    includePending?: boolean;
    includeValid?: boolean;
}

export interface BulkConsentSendResponse {
    message: string;
    data: {
        operationId: string;
        totalDrivers: number;
        skippedCount: number;
        categorization: {
            needsConsent: number;
            hasValidConsent: number;
            hasPendingConsent: number;
            noEmail: number;
        };
        estimatedTime: string;
    };
}

export interface ConsentCategorizationPreviewRequest {
    driverIds?: string[];
    siteIds?: string[];
}

export interface ConsentCategorizationPreviewResponse {
    message: string;
    data: {
        totalDrivers: number;
        categorization: {
            needsConsent: number;
            hasValidConsent: number;
            hasPendingConsent: number;
            noEmail: number;
        };
        willSend: number;
        willSkip: number;
    };
}

export interface RecentCheckValidationRequest {
    driverIds: string[];
    thresholdDays?: number;
}

export interface RecentlyCheckedDriver {
    id: string;
    firstName: string;
    lastName: string;
    licenceNumber: string;
    lastCheckDate: string | null;
    daysSinceLastCheck: number | null;
    checkSucceeded: boolean | null;      // Did the DVLA API call succeed (no error)?
    licenceValid: boolean | null;         // Is the licence itself valid?
    licenceStatus: string | null;         // e.g., "Full", "Disqualified", "Revoked"
}

export interface RecentCheckValidationResponse {
    message: string;
    data: {
        totalDrivers: number;
        recentlyCheckedCount: number;
        needsCheckCount: number;
        thresholdDays: number;
        recentlyCheckedDrivers: RecentlyCheckedDriver[];
        driversNeedingCheck: RecentlyCheckedDriver[];
    };
}

export interface BulkOperationStatusResponse {
    message: string;
    data: {
        operationId: string;
        type: 'licence_check' | 'driver_import' | 'consent_send';
        status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
        progress: number;
        totalItems: number;
        successCount: number;
        failureCount: number;
        progressPercentage: number;
        isComplete: boolean;
        startedAt: Date | null;
        completedAt: Date | null;
        duration: number | null;
        errorMessage: string | null;
        resultSummary: any;
        createdAt: Date;
    };
}

export interface BulkOperationItem {
    id: string;
    driverId: string | null;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    errorMessage: string | null;
    processedAt: Date | null;
    resultData: any;
    driver: Driver | null;
}

export interface BulkOperationResultsResponse {
    message: string;
    data: {
        operation: {
            id: string;
            type: 'licence_check' | 'driver_import' | 'consent_send';
            status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
            totalItems: number;
            successCount: number;
            failureCount: number;
            completedAt: Date | null;
        };
        items: BulkOperationItem[];
    };
}

// ========================================
// REPORT INTERFACES
// ========================================

export type ReportType = 'compliance' | 'risk_analysis' | 'licence_check' | 'email_delivery' | 'audit_activity';

export interface ReportParameters {
    dateRange?: {
        start: string;
        end: string;
    };
    // Compliance specific
    consentStatus?: 'active' | 'expired' | 'expiring_soon' | 'no_consent' | 'all';
    riskLevels?: string[];
    // Risk Analysis specific
    riskLevel?: 'low' | 'medium' | 'high' | 'all';
    minRiskScore?: number;
    maxRiskScore?: number;
    // Licence Check specific
    checkStatus?: 'valid' | 'invalid' | 'expired' | 'unknown' | 'all';
    driverStatus?: 'active' | 'inactive' | 'all';
    // Email Delivery specific
    deliveryStatus?: 'delivered' | 'bounced' | 'dropped' | 'spam' | 'sent' | 'deferred' | 'all';
    emailType?: string | 'all';
    emailStatus?: 'all' | 'delivered' | 'failed' | 'bounced';
    // Audit Activity specific
    activityType?: string | 'all';
    userId?: string;
}

export interface ReportColumn {
    key: string;
    label: string;
    type: 'string' | 'number' | 'date' | 'boolean' | 'badge';
}

export interface ReportData {
    columns: ReportColumn[];
    rows: Record<string, any>[];
    summary?: Record<string, any>;
}

export interface Report {
    id: string;
    type: ReportType;
    reportName: string;
    generatedAt: string;
    generatedBy: {
        id: string;
        name: string;
    };
    rowCount: number;
    status: 'generating' | 'completed' | 'failed';
    isPinned: boolean;
    isOrgWide: boolean;
    expiresAt: string | null;
    daysUntilExpiration: number | null;
}

export interface ReportDetail extends Report {
    parameters: ReportParameters;
    dataSnapshot: ReportData;
    errorMessage?: string | null;
}

export interface GenerateReportResponse {
    message: string;
    data: {
        id: string;
        type: ReportType;
        reportName: string;
        rowCount: number;
        status: 'generating' | 'completed' | 'failed';
        generatedAt: string;
        expiresAt: string | null;
    };
}

export interface ReportHistoryParams {
    reportType?: ReportType | 'all';
    startDate?: string;
    endDate?: string;
    generatedBy?: string;
    page?: number;
    pageSize?: number;
}

export interface ReportHistoryResponse {
    message: string;
    data: Report[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface ReportDetailResponse {
    message: string;
    data: ReportDetail;
}

// Report Schedule interfaces
export interface ReportSchedule {
    id: string;
    type: ReportType;
    reportName: string;
    parameters: ReportParameters;
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    startDateTime?: string | null;
    dayOfWeek?: number | null;
    dayOfMonth?: number | null;
    timeOfDay?: string | null;
    timezone?: string;
    nextRunAt: string;
    lastRunAt: string | null;
    isActive: boolean;
    isOrgWide: boolean;
    createdBy: {
        id: string;
        name: string;
        email: string;
    };
    createdAt: string;
}

export interface CreateScheduleRequest {
    reportType: ReportType;
    reportName: string;
    parameters: ReportParameters;
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    isOrgWide?: boolean;
    startDateTime?: string | null;
    dayOfWeek?: number | null;
    dayOfMonth?: number | null;
    timeOfDay?: string | null;
    timezone?: string;
}

export interface UpdateScheduleRequest {
    reportName?: string;
    parameters?: ReportParameters;
    frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    isActive?: boolean;
    isOrgWide?: boolean;
    startDateTime?: string | null;
    dayOfWeek?: number | null;
    dayOfMonth?: number | null;
    timeOfDay?: string | null;
    timezone?: string;
}

export interface SchedulesResponse {
    message: string;
    data: ReportSchedule[];
}

export interface BulkOperationSummary {
    id: string;
    type: 'licence_check' | 'driver_import' | 'consent_send';
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    totalItems: number;
    successCount: number;
    failureCount: number;
    isComplete: boolean;
    startedAt: Date | null;
    completedAt: Date | null;
    createdAt: Date;
}

export interface BulkOperationsListResponse {
    message: string;
    data: BulkOperationSummary[];
    totalCount: number;
    limit: number;
    offset: number;
}

// User Notification interfaces (for in-app notifications)
export interface UserNotification {
    id: string;
    userId: string;
    organisationId: string;
    type: 'report_ready' | 'report_scheduled' | 'report_failed' | 'system' | 'automated_check_alert' | 'bulk_consent_sent';
    title: string;
    message: string;
    data: Record<string, any> | null;
    isRead: boolean;
    readAt: string | null;
    actionUrl: string | null;
    createdAt: string;
}

export interface UserNotificationsResponse {
    message: string;
    data: {
        notifications: UserNotification[];
        total: number;
        unreadCount: number;
    };
}

export interface UnreadCountResponse {
    message: string;
    data: {
        unreadCount: number;
    };
}

// Notification interfaces
export interface NotificationSettings {
    id: string;
    organisationId: string;
    licenceExpiryEnabled: boolean;
    licenceExpiryDays: number[];
    licenceExpiryRecipients: string[];
    consentExpiryEnabled: boolean;
    consentExpiryDays: number[];
    consentExpiryRecipients: string[];
    complianceAlertsEnabled: boolean;
    complianceSeverity: 'all' | 'medium_high' | 'high_only';
    complianceRecipients: string[];
    complianceCheckTime: string;
    bulkAlertsEnabled: boolean;
    bulkRecipients: string[];
    dailyDigestEnabled: boolean;
    dailyDigestTime: string;
    dailyDigestFrequency: 'daily' | '7days' | '14days' | '30days';
    dailyDigestRecipients: string[];
    dailyDigestIncludeLicenceExpiry: boolean;
    dailyDigestIncludeConsentExpiry: boolean;
    dailyDigestIncludeCompliance: boolean;
    dailyDigestIncludeBulkOperations: boolean;
    consentActionDigestEnabled: boolean;
    consentActionDigestTime: string;
    consentActionDigestRecipients: string[];
    createdAt: string;
    updatedAt: string;
}

export interface NotificationLog {
    id: string;
    organisationId: string;
    notificationType: 'licence_expiry' | 'consent_expiry' | 'compliance_alert' | 'bulk_operation' | 'daily_digest';
    recipient: string;
    subject: string;
    status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced';
    failedReason?: string;
    metadata?: any;
    sentAt?: string;
    deliveredAt?: string;
    createdAt: string;
}

export interface NotificationLogsResponse {
    message: string;
    data: NotificationLog[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

class DVLAApiClient {
    private baseURL: string;
    private authToken: string | null;

    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'https://dvla-licence-checker.azurewebsites.net/api';
        this.authToken = localStorage.getItem('auth_token');
    }

    // Auth methods
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            const response = await fetch(`${this.baseURL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new ApiError(errorData.message || 'Login failed', response.status);
            }

            const data: AuthResponse = await response.json();

            // Store token in localStorage
            this.authToken = data.token;
            localStorage.setItem('auth_token', data.token);

            return data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            // Network or other errors
            throw new ApiError('Network error', 500);
        }
    }

    async logout(): Promise<void> {
        try {
            // Call backend logout endpoint to blacklist the token
            await this.authenticatedRequest('/auth/logout', {
                method: 'POST',
            });
        } catch (error) {
            // Even if backend logout fails, still clear client-side token
            console.error('Backend logout failed:', error);
        } finally {
            // Always clear client-side token
            this.authToken = null;
            localStorage.removeItem('auth_token');
        }
    }

    isAuthenticated(): boolean {
        return !!this.authToken;
    }

    getToken(): string | null {
        return this.authToken;
    }

    // Driver methods
    async getDrivers(params: {
        search?: string;
        page?: number;
        pageSize?: number;
        riskLevel?: string;
        status?: string;
        siteId?: string;
        issue?: string;
        consentStatus?: 'no_consent' | 'expired' | 'expiring_soon';
        clearToDrive?: boolean;
        checkedToday?: boolean;
        minDays?: number;
        maxDays?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        signal?: AbortSignal;
    }): Promise<DriversResponse> {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append('search', params.search);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params.riskLevel && params.riskLevel !== 'all') {
            queryParams.append('riskLevel', params.riskLevel);
        }
        if (params.status) {
            queryParams.append('status', params.status);
        }
        if (params.siteId && params.siteId !== 'all') {
            queryParams.append('siteId', params.siteId);
        }
        if (params.issue) {
            queryParams.append('issue', params.issue);
        }
        if (params.consentStatus) {
            queryParams.append('consentStatus', params.consentStatus);
        }
        if (params.clearToDrive !== undefined) {
            queryParams.append('clearToDrive', params.clearToDrive.toString());
        }
        if (params.checkedToday !== undefined) {
            queryParams.append('checkedToday', params.checkedToday.toString());
        }
        if (params.sortBy) {
            queryParams.append('sortBy', params.sortBy);
        }
        if (params.sortOrder) {
            queryParams.append('sortOrder', params.sortOrder);
        }
        if (params.minDays !== undefined) {
            queryParams.append('minDays', params.minDays.toString());
        }
        if (params.maxDays !== undefined) {
            queryParams.append('maxDays', params.maxDays.toString());
        }

        const response = await this.authenticatedRequest(
            `/drivers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
            { signal: params.signal }
        );

        return response;
    }

    async createDriver(driver: CreateDriverRequest): Promise<Driver> {
        return this.authenticatedRequest('/drivers', {
            method: 'POST',
            body: JSON.stringify(driver),
        });
    }

    async getDriver(id: string): Promise<Driver> {
        return this.authenticatedRequest(`/drivers/${id}`);
    }

    async updateDriver(driverId: string, driver: UpdateDriverRequest): Promise<Driver> {
        try {
            const response = await this.authenticatedRequest(`/drivers/${driverId}`, {
                method: 'PUT',
                body: JSON.stringify(driver),
            });
            return response;
        } catch (error) {
            console.error('Error updating driver:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to update driver', 500);
        }
    }

    /**
     * Deactivate a driver (soft delete)
     * Note: This will also reset the driver's consent. Consent must be re-obtained upon reactivation.
     */
    async deactivateDriver(id: string): Promise<Driver> {
        return this.authenticatedRequest(`/drivers/${id}`, {
            method: 'DELETE',
        });
    }

    /**
     * Reactivate a previously deactivated driver
     * Note: Driver will need to provide consent again after reactivation
     */
    async reactivateDriver(id: string): Promise<{ message: string; data: Driver }> {
        return this.authenticatedRequest(`/drivers/${id}/reactivate`, {
            method: 'PUT',
        });
    }

    // Site methods
    async getSites(params: {
        search?: string;
        active?: boolean;
        page?: number;
        pageSize?: number;
    }): Promise<SitesResponse> {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append('search', params.search);
        if (params.active !== undefined) queryParams.append('active', params.active.toString());
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

        return this.authenticatedRequest(
            `/sites${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
        );
    }

    async getSite(id: string): Promise<{ message: string; data: Site }> {
        return this.authenticatedRequest(`/sites/${id}`);
    }

    async createSite(site: CreateSiteRequest): Promise<{ message: string; data: Site }> {
        return this.authenticatedRequest('/sites', {
            method: 'POST',
            body: JSON.stringify(site),
        });
    }

    async updateSite(id: string, site: UpdateSiteRequest): Promise<{ message: string; data: Site }> {
        return this.authenticatedRequest(`/sites/${id}`, {
            method: 'PUT',
            body: JSON.stringify(site),
        });
    }

    async deleteSite(
        id: string,
        options?: { action?: 'unassign' | 'reassign'; targetSiteId?: string }
    ): Promise<{ message: string }> {
        return this.authenticatedRequest(`/sites/${id}`, {
            method: 'DELETE',
            body: options ? JSON.stringify(options) : undefined,
        });
    }

    async getSiteDrivers(siteId: string, params?: {
        page?: number;
        pageSize?: number;
    }): Promise<DriversResponse> {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

        return this.authenticatedRequest(
            `/sites/${siteId}/drivers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
        );
    }

    // Site Risk methods

    /**
     * Get site risk leaderboard for all sites in the organisation
     * Returns sites ranked by risk score (lowest = best)
     */
    async getSiteRiskLeaderboard(): Promise<SiteRiskLeaderboardResponse> {
        return this.authenticatedRequest('/sites/risk/leaderboard');
    }

    /**
     * Get detailed risk score breakdown for a specific site
     */
    async getSiteRiskScore(siteId: string): Promise<SiteRiskScoreResponse> {
        return this.authenticatedRequest(`/sites/${siteId}/risk`);
    }

    /**
     * Get historical risk trend for a specific site
     */
    async getSiteRiskTrend(siteId: string, days: number = 30): Promise<SiteRiskTrendResponse> {
        return this.authenticatedRequest(`/sites/${siteId}/risk/history?days=${days}`);
    }

    /**
     * Get aggregate trends for all sites (for dashboard Trends tab charts)
     */
    async getSiteRiskTrends(days: number = 30): Promise<SiteRiskTrendsResponse> {
        return this.authenticatedRequest(`/sites/risk/trends?days=${days}`);
    }

    /**
     * Get filtered driver IDs for a site based on risk criteria
     * Used for clickable tiles in site risk breakdown
     * Filter values come from BreakdownCategoryDefinition.filter (backend-defined)
     */
    async getSiteRiskFilteredDrivers(
        siteId: string,
        filter: string,
        signal?: AbortSignal
    ): Promise<{ message: string; data: string[]; count: number }> {
        return this.authenticatedRequest(`/sites/${siteId}/risk/drivers?filter=${filter}`, { signal });
    }

    // Licence Check methods

    // New Licence Check
    async performLicenceCheck(driverId: string): Promise<LicenceCheckResponse> {
        try {
            const response = await this.authenticatedRequest('/licence-checks', {
                method: 'POST',
                body: JSON.stringify({ driverId }),
            });
            return response;
        } catch (error) {
            console.error('Error performing licence check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to perform licence check', 500);
        }
    }

    // Get latest licence check for a driver
    async getLatestLicenceCheck(driverId: string): Promise<LatestLicenceCheckResponse> {
        try {
            const response = await this.authenticatedRequest(`/licence-checks/driver/${driverId}/latest`);
            return response;
        } catch (error) {
            console.error('Error getting latest licence check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get latest licence check', 500);
        }
    }

    // Get licence check history for a driver
    async getLicenceCheckHistory(
        driverId: string,
        page: number = 1,
        pageSize: number = 10
    ): Promise<LicenceCheckHistoryResponse> {
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString()
            });

            const response = await this.authenticatedRequest(
                `/licence-checks/driver/${driverId}/history?${queryParams.toString()}`
            );
            return response;
        } catch (error) {
            console.error('Error getting licence check history:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get licence check history', 500);
        }
    }

    // Get drivers due for licence check
    async getDriversDueForCheck(signal?: AbortSignal): Promise<{
        message: string;
        data: Array<{
            id: string;
            firstName: string;
            lastName: string;
            licenceNumber: string;
            riskLevel: string;
            daysSinceLastCheck: number;
            lastCheckDate?: string;
        }>;
    }> {
        try {
            const response = await this.authenticatedRequest('/licence-checks/due', { signal });
            return response;
        } catch (error) {
            console.error('Error getting drivers due for check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get drivers due for check', 500);
        }
    }

    // Get licence check statistics (used or dashboard)
    async getLicenceCheckStats(): Promise<{
        message: string;
        data: {
            totalDrivers: number;
            totalChecks: number;
            checksThisMonth: number;
            riskLevels: {
                low: number;
                medium: number;
                high: number;
            };
            recentActivity: Array<{
                driverId: string;
                driverName: string;
                checkDate: string;
                riskLevel: string;
            }>;
        };
    }> {
        try {
            const response = await this.authenticatedRequest('/licence-checks/stats');
            return response;
        } catch (error) {
            console.error('Error getting licence check stats:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get licence check statistics', 500);
        }
    }

    // Get scoring reference for the organisation's fleet subtype
    async getScoringReference(): Promise<any> {
        try {
            return await this.authenticatedRequest('/settings/licence-check-automation/scoring-reference');
        } catch (error) {
            console.error('Error getting scoring reference:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get scoring reference', 500);
        }
    }

    // Validate licence number
    async validateLicenceNumber(licenceNumber: string): Promise<{
        message: string;
        data: {
            valid: boolean;
            formatted: string;
            errors?: string[];
        };
    }> {
        try {
            const response = await this.authenticatedRequest('/licence-checks/validate-licence', {
                method: 'POST',
                body: JSON.stringify({
                    licenceNumber
                }),
            });
            return response;
        } catch (error) {
            console.error('Error validating licence number:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to validate licence number', 500);
        }
    }

    // Token validation method
    async validateToken(): Promise<AuthResponse['user']> {
        try {
            const response = await this.authenticatedRequest('/auth/me');
            return response.user;
        } catch (error) {
            // Token is invalid, clear it
            this.logout();
            throw error;
        }
    }

    // Token refresh method
    async refreshToken(): Promise<{ token: string; expiresAt: string }> {
        try {
            const response = await this.authenticatedRequest('/auth/refresh', {
                method: 'POST',
            });

            // Update stored token
            this.authToken = response.token;
            localStorage.setItem('auth_token', response.token);

            return {
                token: response.token,
                expiresAt: response.expiresAt
            };
        } catch (error) {
            // If refresh fails, logout the user
            if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                this.logout();
            }
            throw error;
        }
    }

    // Dashboard methods
    async getDashboardStats(): Promise<DashboardStats> {
        const response = await this.authenticatedRequest('/licence-checks/stats');
        const backendData = response.data;

        return {
            totalDrivers: backendData.totalDrivers,
            activeDrivers: backendData.totalDrivers,
            riskLevels: backendData.riskLevels,
            checksThisMonth: backendData.checksLastMonth || 0,
            checksToday: backendData.checksToday || 0,
            averageRiskScore: backendData.averageRiskScore || 0,
            driversWithConsent: backendData.driversWithConsent || 0,
            driversWithoutConsent: backendData.driversWithoutConsent || 0,
            driversEverChecked: backendData.driversEverChecked || 0,
            driversWithRecentChecks: backendData.driversWithRecentChecks || 0,
            consentExpiring: backendData.driversDueForCheck || 0,
            clearToDrive: backendData.clearToDrive || 0,
            notClearToDrive: backendData.notClearToDrive || 0,
            lastUpdated: backendData.lastUpdated
        };
    }

    async getDriversDue(): Promise<DriverDue[]> {
        const response = await this.authenticatedRequest('/licence-checks/due');
        // Backend returns empty array when no drivers due
        return response.data || [];
    }

    async getRecentActivity(): Promise<RecentActivity[]> {
        const response = await this.authenticatedRequest('/licence-checks/recent');
        // Backend returns array of recent activities
        return response.data || [];
    }

    // Consent methods
    async getConsentForm(token: string): Promise<ConsentFormData> {
        const response = await fetch(`${this.baseURL}/consent/${token}`);

        if (!response.ok) {
            const errorData = await response.json();
            throw new ApiError(errorData.message || 'Failed to get consent form', response.status);
        }

        const result = await response.json();
        return result.data;
    }

    async submitConsent(token: string, consentData: ConsentSubmissionData): Promise<{ message: string }> {
        const response = await fetch(`${this.baseURL}/consent/${token}/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(consentData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new ApiError(errorData.message || 'Failed to submit consent', response.status);
        }

        return response.json();
    }

    async withdrawConsent(token: string, reason: string): Promise<{ message: string }> {
        const response = await fetch(`${this.baseURL}/consent/${token}/withdraw`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new ApiError(errorData.message || 'Failed to withdraw consent', response.status);
        }

        return response.json();
    }

    // Protected consent management endpoints (authentication required)
    async sendConsentRequest(request: SendConsentRequest): Promise<{ message: string; token: string }> {
        return this.authenticatedRequest('/consent/send', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }

    async getConsentStatus(driverId: string): Promise<ConsentStatusResponse> {
        const response = await this.authenticatedRequest(`/consent/status/${driverId}`);
        return response.data;
    }

    async sendConsentReminder(driverId: string): Promise<{ message: string }> {
        return this.authenticatedRequest(`/consent/reminder/${driverId}`, {
            method: 'POST',
        });
    }

    async adminWithdrawConsent(driverId: string, reason: string): Promise<{ message: string }> {
        return this.authenticatedRequest(`/consent/admin/${driverId}/withdraw`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        });
    }

    async recordDriverConsent(driverId: string, consentData: {
        consentReference: string;
        consentDate: string;
        consentMethod?: string;
    }): Promise<void> {
        try {
            await this.authenticatedRequest(`/drivers/${driverId}/consent`, {
                method: 'POST',
                body: JSON.stringify(consentData)
            });
        } catch (error) {
            console.error('Failed to record driver consent:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to record driver consent', 500);
        }
    }

    async getSystemHealth(): Promise<SystemHealthResponse> {
        try {
            const response = await this.authenticatedRequest('/jobs/health');
            return response;
        } catch (error) {
            console.error('Error getting system health:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get system health', 500);
        }
    }

    async getJobStats(): Promise<JobStatsResponse> {
        try {
            const response = await this.authenticatedRequest('/jobs/stats');
            return response;
        } catch (error) {
            console.error('Error getting job stats:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get job stats', 500);
        }
    }

    async triggerExpiryCheck(): Promise<ManualJobResponse> {
        try {
            const response = await this.authenticatedRequest('/jobs/expiry-check', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering expiry check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger expiry check', 500);
        }
    }

    async triggerEmailRetry(): Promise<ManualJobResponse> {
        try {
            const response = await this.authenticatedRequest('/jobs/email-retry', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering email retry:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger email retry', 500);
        }
    }

    async triggerTokenCleanup(): Promise<ManualJobResponse> {
        try {
            const response = await this.authenticatedRequest('/jobs/token-cleanup', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering token cleanup:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger token cleanup', 500);
        }
    }

    // Email Analytics methods
    async getEmailAnalytics(): Promise<EmailAnalyticsResponse> {
        try {
            const response = await this.authenticatedRequest('/email/analytics');
            return response;
        } catch (error) {
            console.error('Error getting email analytics:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get email analytics', 500);
        }
    }

    async getDriverEmailHistory(driverId: string): Promise<DriverEmailHistoryResponse> {
        try {
            const response = await this.authenticatedRequest(`/email/history/${driverId}`);
            return response;
        } catch (error) {
            console.error('Error getting driver email history:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get driver email history', 500);
        }
    }

    // Bulk Operations methods
    async uploadCSV(file: File): Promise<CSVUploadResponse> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = this.authToken || localStorage.getItem('auth_token');
            if (!token) {
                throw new ApiError('No authentication token found', 401);
            }

            const response = await fetch(`${this.baseURL}/bulk/upload-csv`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(errorData.error || 'Failed to upload CSV', response.status);
            }

            return response.json();
        } catch (error) {
            console.error('Error uploading CSV:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to upload CSV', 500);
        }
    }

    async importDriversFromCSV(request: BulkImportRequest): Promise<BulkOperationResponse> {
        try {
            const response = await this.authenticatedRequest('/bulk/import-drivers', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response;
        } catch (error) {
            console.error('Error importing drivers:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to import drivers', 500);
        }
    }

    async validateRecentChecks(request: RecentCheckValidationRequest): Promise<RecentCheckValidationResponse> {
        try {
            const response = await this.authenticatedRequest('/bulk/validate-recent-checks', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response;
        } catch (error) {
            console.error('Error validating recent checks:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to validate recent checks', 500);
        }
    }

    async startBulkLicenceCheck(request: BulkLicenceCheckRequest): Promise<BulkOperationResponse> {
        try {
            const response = await this.authenticatedRequest('/bulk/check-licences', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response;
        } catch (error) {
            console.error('Error starting bulk licence check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to start bulk licence check', 500);
        }
    }

    async startBulkConsentSend(request: BulkConsentSendRequest): Promise<BulkConsentSendResponse> {
        try {
            const response = await this.authenticatedRequest('/bulk/send-consent', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response;
        } catch (error) {
            console.error('Error starting bulk consent send:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to start bulk consent send', 500);
        }
    }

    async previewConsentCategorization(request: ConsentCategorizationPreviewRequest): Promise<ConsentCategorizationPreviewResponse> {
        try {
            const response = await this.authenticatedRequest('/bulk/preview-consent', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response;
        } catch (error) {
            console.error('Error previewing consent categorization:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to preview consent categorization', 500);
        }
    }

    async getBulkOperationStatus(operationId: string): Promise<BulkOperationStatusResponse> {
        try {
            const response = await this.authenticatedRequest(`/bulk/operations/${operationId}`, {
                method: 'GET',
            });
            return response;
        } catch (error) {
            console.error('Error getting bulk operation status:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get bulk operation status', 500);
        }
    }

    async getBulkOperationResults(operationId: string): Promise<BulkOperationResultsResponse> {
        try {
            const response = await this.authenticatedRequest(`/bulk/operations/${operationId}/items`, {
                method: 'GET',
            });
            return response;
        } catch (error) {
            console.error('Error getting bulk operation results:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get bulk operation results', 500);
        }
    }

    async getBulkOperations(params?: {
        type?: 'licence_check' | 'driver_import' | 'consent_send';
        status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
        limit?: number;
        offset?: number;
    }): Promise<BulkOperationsListResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.type) queryParams.append('type', params.type);
            if (params?.status) queryParams.append('status', params.status);
            if (params?.limit) queryParams.append('limit', params.limit.toString());
            if (params?.offset) queryParams.append('offset', params.offset.toString());

            const queryString = queryParams.toString();
            const url = `/bulk/operations${queryString ? `?${queryString}` : ''}`;

            const response = await this.authenticatedRequest(url, {
                method: 'GET',
            });
            return response;
        } catch (error) {
            console.error('Error getting bulk operations:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get bulk operations', 500);
        }
    }

    async downloadCSVTemplate(): Promise<Blob> {
        try {
            const token = this.authToken || localStorage.getItem('auth_token');
            if (!token) {
                throw new ApiError('No authentication token found', 401);
            }

            const response = await fetch(`${this.baseURL}/bulk/template`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new ApiError('Failed to download template', response.status);
            }

            return response.blob();
        } catch (error) {
            console.error('Error downloading CSV template:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to download CSV template', 500);
        }
    }

    // User Management methods
    async getUsers(params: {
        search?: string;
        page?: number;
        limit?: number;
        role?: string;
        isActive?: boolean;
    }): Promise<UsersResponse> {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.role && params.role !== 'all') queryParams.append('role', params.role);
        if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

        const response = await this.authenticatedRequest(
            `/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
        );

        return response;
    }

    async getUserById(id: string): Promise<User> {
        return this.authenticatedRequest(`/users/${id}`);
    }

    async createUser(userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
        password?: string;
    }): Promise<{ message: string; data: User; temporaryPassword?: string }> {
        return this.authenticatedRequest('/users', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    async inviteUser(userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }): Promise<{ message: string; data: User; invitationToken?: string }> {
        return this.authenticatedRequest('/users/invite', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    async updateUser(id: string, userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }): Promise<{ message: string; data: User }> {
        return this.authenticatedRequest(`/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(userData),
        });
    }

    async deleteUser(id: string): Promise<{ message: string }> {
        return this.authenticatedRequest(`/users/${id}`, {
            method: 'DELETE',
        });
    }

    async reactivateUser(id: string): Promise<{ message: string; data: User }> {
        return this.authenticatedRequest(`/users/${id}/reactivate`, {
            method: 'PATCH',
        });
    }

    async resetUserPassword(id: string, options: { sendEmail: boolean }): Promise<{
        message: string;
        temporaryPassword?: string;
        data: { emailSent: boolean };
    }> {
        return this.authenticatedRequest(`/users/${id}/reset-password`, {
            method: 'POST',
            body: JSON.stringify(options),
        });
    }

    async updateUserRole(id: string, role: string): Promise<{ message: string; data: User }> {
        return this.authenticatedRequest(`/users/${id}/role`, {
            method: 'PUT',
            body: JSON.stringify({ role }),
        });
    }

    async getUserActivity(id: string, limit: number = 20): Promise<{
        message: string;
        data: Array<{
            id: string;
            action: string;
            entityType: string;
            entityId: string;
            previousValue?: any;
            newValue?: any;
            ipAddress?: string;
            timestamp: Date;
            user: {
                firstName: string;
                lastName: string;
            };
        }>;
    }> {
        return this.authenticatedRequest(`/users/${id}/activity?limit=${limit}`);
    }

    async resendInvitation(id: string): Promise<{
        message: string;
        data: { invitationExpiry: Date };
    }> {
        return this.authenticatedRequest(`/users/${id}/resend-invitation`, {
            method: 'POST',
        });
    }

    async cancelInvitation(id: string): Promise<{ message: string }> {
        return this.authenticatedRequest(`/users/${id}/cancel-invitation`, {
            method: 'DELETE',
        });
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<{
        message: string;
        passwordExpiry: Date;
    }> {
        return this.authenticatedRequest('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({ currentPassword, newPassword }),
        });
    }

    // Notification methods
    async getNotificationSettings(): Promise<{ message: string; data: NotificationSettings }> {
        try {
            const response = await this.authenticatedRequest('/notifications/settings');
            return response;
        } catch (error) {
            console.error('Error getting notification settings:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get notification settings', 500);
        }
    }

    async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<{ message: string; data: NotificationSettings }> {
        try {
            const response = await this.authenticatedRequest('/notifications/settings', {
                method: 'PUT',
                body: JSON.stringify(settings),
            });
            return response;
        } catch (error) {
            console.error('Error updating notification settings:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to update notification settings', 500);
        }
    }

    async getNotificationLogs(params: {
        page?: number;
        limit?: number;
        type?: string;
        status?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<NotificationLogsResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.limit) queryParams.append('limit', params.limit.toString());
            if (params.type) queryParams.append('type', params.type);
            if (params.status) queryParams.append('status', params.status);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);

            const response = await this.authenticatedRequest(
                `/notifications/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
            );
            return response;
        } catch (error) {
            console.error('Error getting notification logs:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get notification logs', 500);
        }
    }

    async retryNotification(logId: string): Promise<{ message: string }> {
        try {
            const response = await this.authenticatedRequest(`/notifications/retry/${logId}`, {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error retrying notification:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to retry notification', 500);
        }
    }

    async sendTestNotification(email: string): Promise<{ message: string }> {
        try {
            const response = await this.authenticatedRequest('/notifications/test', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            return response;
        } catch (error) {
            console.error('Error sending test notification:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to send test notification', 500);
        }
    }

    async triggerLicenceExpiryNotifications(): Promise<{ 
        message: string; 
        data: { sent: number; failed: number } 
    }> {
        try {
            const response = await this.authenticatedRequest('/notifications/trigger/licence-expiry', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering licence expiry notifications:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger licence expiry notifications', 500);
        }
    }

    async triggerConsentExpiryNotifications(): Promise<{ 
        message: string; 
        data: { sent: number; failed: number } 
    }> {
        try {
            const response = await this.authenticatedRequest('/notifications/trigger/consent-expiry', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering consent expiry notifications:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger consent expiry notifications', 500);
        }
    }

    async triggerDailyDigest(): Promise<{ 
        message: string; 
        data: { recipients: string[]; sentAt: string } 
    }> {
        try {
            const response = await this.authenticatedRequest('/notifications/trigger/daily-digest', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering daily digest:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger daily digest', 500);
        }
    }

    async triggerComplianceCheck(): Promise<{
        message: string;
        data: { sent: number; failed: number }
    }> {
        try {
            const response = await this.authenticatedRequest('/notifications/trigger/compliance-check', {
                method: 'POST',
            });
            return response;
        } catch (error) {
            console.error('Error triggering compliance check:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to trigger compliance check', 500);
        }
    }

    // Helper method for authenticated requests
    private async authenticatedRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
        // Always use localStorage as the single source of truth
        // This prevents using stale in-memory tokens after logout
        const token = localStorage.getItem('auth_token');

        if (!token) {
            // If no token in localStorage, also clear in-memory token
            this.authToken = null;
            throw new ApiError('No authentication token found', 401);
        }

        // Sync in-memory token with localStorage
        if (this.authToken !== token) {
            this.authToken = token;
        }

        const isFormData = options.body instanceof FormData;

        const response = await fetch(`${this.baseURL}${endpoint}`, {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                // Omit Content-Type for FormData - browser must set it with the multipart boundary
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                ...options.headers,
            },
        });

        if (response.status === 401) {
            localStorage.removeItem('auth_token');
            this.authToken = null;
            throw new ApiError('Session expired. Please login again.', 401);
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new ApiError(errorData.error || errorData.message || `Request failed with status ${response.status}`, response.status);
        }

        // Handles different response types (JSON, empty, error, etc)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }

        return null;
    }

    // ========================================
    // REPORT METHODS
    // ========================================

    /**
     * Generate a new report
     */
    async generateReport(reportType: ReportType, reportName: string, parameters: ReportParameters): Promise<GenerateReportResponse> {
        try {
            const response = await this.authenticatedRequest('/reports/generate', {
                method: 'POST',
                body: JSON.stringify({
                    reportType,
                    reportName,
                    parameters
                })
            });
            return response;
        } catch (error) {
            console.error('Error generating report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to generate report', 500);
        }
    }

    /**
     * Get report history with filters and pagination
     */
    async getReportHistory(params: ReportHistoryParams): Promise<ReportHistoryResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (params.reportType) queryParams.append('reportType', params.reportType);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.generatedBy) queryParams.append('generatedBy', params.generatedBy);
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

            const response = await this.authenticatedRequest(`/reports?${queryParams.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching report history:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to fetch report history', 500);
        }
    }

    /**
     * Get a single report by ID
     */
    async getReport(reportId: string): Promise<ReportDetailResponse> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}`);
            return response;
        } catch (error) {
            console.error('Error fetching report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to fetch report', 500);
        }
    }

    /**
     * Pin or unpin a report
     */
    async pinReport(reportId: string, isPinned: boolean): Promise<{ message: string; data: { id: string; isPinned: boolean; expiresAt: string | null } }> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}/pin`, {
                method: 'PATCH',
                body: JSON.stringify({ isPinned })
            });
            return response;
        } catch (error) {
            console.error('Error pinning/unpinning report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to pin/unpin report', 500);
        }
    }

    /**
     * Delete a report
     */
    async deleteReport(reportId: string): Promise<{ message: string }> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}`, {
                method: 'DELETE'
            });
            return response;
        } catch (error) {
            console.error('Error deleting report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to delete report', 500);
        }
    }

    /**
     * Export report as CSV
     */
    async exportReportCSV(reportId: string, reportName: string): Promise<void> {
        try {
            const token = this.authToken || localStorage.getItem('auth_token');
            if (!token) {
                throw new ApiError('No authentication token found', 401);
            }

            const response = await fetch(`${this.baseURL}/reports/${reportId}/export/csv`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new ApiError('Failed to export report', response.status);
            }

            // Download the file
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting report to CSV:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to export report', 500);
        }
    }

    /**
     * Export report as PDF
     */
    async exportReportPDF(reportId: string, reportName: string): Promise<void> {
        try {
            const token = this.authToken || localStorage.getItem('auth_token');
            if (!token) {
                throw new ApiError('No authentication token found', 401);
            }

            const response = await fetch(`${this.baseURL}/reports/${reportId}/export/pdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new ApiError('Failed to export report', response.status);
            }

            // Download the file
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting report to PDF:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to export report', 500);
        }
    }

    /**
     * Regenerate a report with the same parameters
     */
    async regenerateReport(reportId: string): Promise<GenerateReportResponse> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}/regenerate`, {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error regenerating report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to regenerate report', 500);
        }
    }

    /**
     * Share a report with other users
     */
    async shareReport(reportId: string, userIds: string[], note?: string, canEdit?: boolean): Promise<{ message: string; data: any }> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}/share`, {
                method: 'POST',
                body: JSON.stringify({
                    userIds,
                    note,
                    canEdit
                })
            });
            return response;
        } catch (error) {
            console.error('Error sharing report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to share report', 500);
        }
    }

    /**
     * Get all users a report is shared with
     */
    async getReportShares(reportId: string): Promise<{ data: any[] }> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}/shares`);
            return response;
        } catch (error) {
            console.error('Error getting report shares:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get report shares', 500);
        }
    }

    /**
     * Revoke share for a specific user
     */
    async revokeReportShare(reportId: string, userId: string): Promise<{ message: string }> {
        try {
            const response = await this.authenticatedRequest(`/reports/${reportId}/shares/${userId}`, {
                method: 'DELETE'
            });
            return response;
        } catch (error) {
            console.error('Error revoking share:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to revoke share', 500);
        }
    }

    /**
     * Get all reports shared with the current user
     */
    async getSharedWithMeReports(params: ReportHistoryParams): Promise<ReportHistoryResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (params.reportType) queryParams.append('reportType', params.reportType);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

            const response = await this.authenticatedRequest(`/reports/shared-with-me?${queryParams.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching shared reports:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to fetch shared reports', 500);
        }
    }

    // ============ User Notifications ============

    /**
     * Get user notifications
     */
    async getUserNotifications(params?: {
        unreadOnly?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<{ notifications: UserNotification[]; total: number; unreadCount: number }> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.unreadOnly) queryParams.append('unreadOnly', 'true');
            if (params?.limit) queryParams.append('limit', params.limit.toString());
            if (params?.offset) queryParams.append('offset', params.offset.toString());

            const response: UserNotificationsResponse = await this.authenticatedRequest(
                `/user-notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
                { method: 'GET' }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting user notifications:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get notifications', 500);
        }
    }

    /**
     * Get unread notification count
     */
    async getUnreadNotificationCount(): Promise<{ unreadCount: number }> {
        try {
            const response: UnreadCountResponse = await this.authenticatedRequest('/user-notifications/unread-count', {
                method: 'GET'
            });
            return response.data;
        } catch (error) {
            console.error('Error getting unread count:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get unread count', 500);
        }
    }

    /**
     * Mark a notification as read
     */
    async markNotificationAsRead(notificationId: string): Promise<void> {
        try {
            await this.authenticatedRequest(`/user-notifications/${notificationId}/read`, {
                method: 'PATCH'
            });
        } catch (error) {
            console.error('Error marking notification as read:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to mark notification as read', 500);
        }
    }

    /**
     * Mark all notifications as read
     */
    async markAllNotificationsAsRead(): Promise<{ count: number }> {
        try {
            const response = await this.authenticatedRequest('/user-notifications/read-all', {
                method: 'PATCH'
            });
            return response.data;
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to mark all notifications as read', 500);
        }
    }

    // ============ Report Schedules ============

    /**
     * Get all report schedules
     */
    async getReportSchedules(params?: { reportType?: ReportType; isActive?: boolean }): Promise<ReportSchedule[]> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.reportType) queryParams.append('reportType', params.reportType);
            if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

            const response: SchedulesResponse = await this.authenticatedRequest(
                `/reports/schedules${queryParams.toString() ? '?' + queryParams.toString() : ''}`,
                { method: 'GET' }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting report schedules:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get report schedules', 500);
        }
    }

    /**
     * Get a single report schedule
     */
    async getReportSchedule(scheduleId: string): Promise<ReportSchedule> {
        try {
            const response = await this.authenticatedRequest(`/reports/schedules/${scheduleId}`, {
                method: 'GET'
            });
            return response.data;
        } catch (error) {
            console.error('Error getting report schedule:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get report schedule', 500);
        }
    }

    /**
     * Create a new report schedule
     */
    async createReportSchedule(data: CreateScheduleRequest): Promise<ReportSchedule> {
        try {
            const response = await this.authenticatedRequest('/reports/schedules', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            return response.data;
        } catch (error) {
            console.error('Error creating report schedule:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to create report schedule', 500);
        }
    }

    /**
     * Update a report schedule
     */
    async updateReportSchedule(scheduleId: string, data: UpdateScheduleRequest): Promise<ReportSchedule> {
        try {
            const response = await this.authenticatedRequest(`/reports/schedules/${scheduleId}`, {
                method: 'PATCH',
                body: JSON.stringify(data)
            });
            return response.data;
        } catch (error) {
            console.error('Error updating report schedule:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to update report schedule', 500);
        }
    }

    /**
     * Delete a report schedule
     */
    async deleteReportSchedule(scheduleId: string): Promise<void> {
        try {
            await this.authenticatedRequest(`/reports/schedules/${scheduleId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error('Error deleting report schedule:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to delete report schedule', 500);
        }
    }

    /**
     * Run a scheduled report immediately
     */
    async runScheduleNow(scheduleId: string): Promise<Report> {
        try {
            const response = await this.authenticatedRequest(`/reports/schedules/${scheduleId}/run`, {
                method: 'POST'
            });
            return response.data;
        } catch (error) {
            console.error('Error running scheduled report:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to run scheduled report', 500);
        }
    }

    // ========================================
    // AUTOMATED LICENCE CHECKING METHODS
    // ========================================

    /**
     * Get automation settings for the current organization
     */
    async getAutomationSettings(): Promise<LicenceCheckSettings> {
        try {
            const response = await this.authenticatedRequest('/settings/licence-check-automation');
            return response;
        } catch (error) {
            console.error('Error getting automation settings:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get automation settings', 500);
        }
    }

    /**
     * Update automation settings
     */
    async updateAutomationSettings(settings: UpdateLicenceCheckSettingsRequest): Promise<LicenceCheckSettings> {
        try {
            const response = await this.authenticatedRequest('/settings/licence-check-automation', {
                method: 'POST',
                body: JSON.stringify(settings)
            });
            return response;
        } catch (error) {
            console.error('Error updating automation settings:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to update automation settings', 500);
        }
    }

    /**
     * Test scheduler - preview which drivers would be checked
     */
    async testAutomationScheduler(): Promise<TestSchedulerResponse> {
        try {
            const response = await this.authenticatedRequest('/settings/licence-check-automation/test', {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error testing automation scheduler:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to test automation scheduler', 500);
        }
    }

    /**
     * Trigger immediate automated check (manual run)
     */
    async runAutomationNow(): Promise<RunNowResponse> {
        try {
            const response = await this.authenticatedRequest('/settings/licence-check-automation/run-now', {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error running automation now:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to run automation now', 500);
        }
    }

    // SCORING CONFIGURATION METHODS
    // ========================================

    /**
     * Get scoring configuration for the current organization
     */
    async getScoringConfig(): Promise<ScoringConfigResponse> {
        try {
            const response = await this.authenticatedRequest('/settings/scoring-config');
            return response;
        } catch (error) {
            console.error('Error getting scoring config:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to get scoring configuration', 500);
        }
    }

    /**
     * Update scoring configuration
     */
    async updateScoringConfig(updates: UpdateScoringConfigRequest): Promise<ScoringConfigUpdateResponse> {
        try {
            const response = await this.authenticatedRequest('/settings/scoring-config', {
                method: 'PATCH',
                body: JSON.stringify(updates)
            });
            return response;
        } catch (error) {
            console.error('Error updating scoring config:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to update scoring configuration', 500);
        }
    }

    /**
     * Reset all scoring configuration to defaults
     */
    async resetScoringConfig(): Promise<ScoringConfigResetResponse> {
        try {
            const response = await this.authenticatedRequest('/settings/scoring-config/reset', {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error resetting scoring config:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to reset scoring configuration', 500);
        }
    }

    /**
     * Reset a single field to default
     */
    async resetScoringField(fieldName: string): Promise<ScoringConfigResetResponse> {
        try {
            const response = await this.authenticatedRequest(`/settings/scoring-config/reset/${fieldName}`, {
                method: 'POST'
            });
            return response;
        } catch (error) {
            console.error('Error resetting scoring field:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to reset scoring field', 500);
        }
    }

    // ORGANISATION METHODS
    // ========================================

    /**
     * Get the current user's organisation details
     */
    async getOrganisation(): Promise<any> {
        try {
            return await this.authenticatedRequest('/organisation');
        } catch (error) {
            console.error('Error loading organisation:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to load organisation', 500);
        }
    }

    /**
     * Upload an organisation logo
     */
    async uploadOrganisationLogo(formData: FormData): Promise<any> {
        try {
            return await this.authenticatedRequest('/organisation/logo', {
                method: 'POST',
                body: formData,
            });
        } catch (error) {
            console.error('Error uploading organisation logo:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to upload logo', 500);
        }
    }

    /**
     * Delete the organisation logo
     */
    async deleteOrganisationLogo(): Promise<void> {
        try {
            await this.authenticatedRequest('/organisation/logo', {
                method: 'DELETE',
            });
        } catch (error) {
            console.error('Error deleting organisation logo:', error);
            if (error instanceof ApiError) throw error;
            throw new ApiError('Failed to delete logo', 500);
        }
    }
}

// Create a singleton instance
export const apiClient = new DVLAApiClient();

// Custom error class
class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}